import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaComments,
  FaTimes,
  FaMinus,
  FaExpand,
  FaCompress,
  FaRocket,
  FaShieldAlt,
  FaBell,
} from "react-icons/fa";
import { FiMessageCircle, FiSend } from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import { useChat } from "../../context/ChatContext";
import ChatWindow from "./ChatWindow";
import toast from "react-hot-toast";

const ChatWidget = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const {
    isChatOpen,
    toggleChat,
    unreadCount,
    isConnected,
    isMuted,
    isBanned,
  } = useChat();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const notificationTimeoutRef = useRef(null);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768 && isExpanded) {
        setIsExpanded(false);
      }
      if (window.innerWidth > 768 && isMinimized) {
        setIsMinimized(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isExpanded, isMinimized]);

  // Handle new message notification
  useEffect(() => {
    if (unreadCount > 0 && !isChatOpen) {
      setShowNotification(true);
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      notificationTimeoutRef.current = setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    }
  }, [unreadCount, isChatOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // Listen for custom openChat event
  useEffect(() => {
    const handleOpenChat = () => {
      toggleChat();
    };
    window.addEventListener("openChat", handleOpenChat);
    return () => window.removeEventListener("openChat", handleOpenChat);
  }, [toggleChat]);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.9)" : "rgba(255, 255, 255, 0.95)";

  // Don't show chat widget if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  // Don't show chat widget if user is banned
  if (isBanned) {
    return null;
  }

  const handleOpenChat = () => {
    toggleChat();
    setShowNotification(false);
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Get chat dimensions based on device and state
  const getChatDimensions = () => {
    if (isMobile) {
      if (isExpanded) {
        return {
          width: "100vw",
          height: "100vh",
          bottom: 0,
          right: 0,
          borderRadius: 0,
        };
      }
      if (isMinimized) {
        return {
          width: "280px",
          height: "56px",
          bottom: "16px",
          right: "16px",
          borderRadius: "16px",
        };
      }
      return {
        width: "calc(100vw - 32px)",
        height: "70vh",
        bottom: "16px",
        right: "16px",
        borderRadius: "20px",
      };
    }
    if (isExpanded) {
      return {
        width: "min(90vw, 1200px)",
        height: "min(85vh, 800px)",
        bottom: "20px",
        right: "20px",
        borderRadius: "20px",
      };
    }
    if (isMinimized) {
      return {
        width: "340px",
        height: "56px",
        bottom: "20px",
        right: "20px",
        borderRadius: "16px",
      };
    }
    return {
      width: "420px",
      height: "620px",
      bottom: "20px",
      right: "20px",
      borderRadius: "24px",
    };
  };

  const dimensions = getChatDimensions();

  // Animation variants
  const buttonVariants = {
    initial: { scale: 0 },
    animate: { scale: 1 },
    hover: { scale: 1.1, boxShadow: "0 8px 25px rgba(139,92,246,0.4)" },
    tap: { scale: 0.95 },
  };

  const notificationVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0, opacity: 0 },
  };

  const chatVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", damping: 25, stiffness: 300 },
    },
    exit: { opacity: 0, y: 50, scale: 0.9 },
  };

  return (
    <>
      {/* Chat Widget Button */}
      <motion.div
        className="chat-widget-toggle"
        variants={buttonVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
        whileTap="tap"
        style={{
          position: "fixed",
          bottom: isMobile ? "16px" : "20px",
          right: isMobile ? "16px" : "20px",
          zIndex: 1000,
        }}
      >
        {/* Notification Badge */}
        <AnimatePresence>
          {showNotification && unreadCount > 0 && (
            <motion.div
              variants={notificationVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "white",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: "bold",
                zIndex: 1001,
                boxShadow: "0 2px 8px rgba(239,68,68,0.4)",
              }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={handleOpenChat}
          style={{
            width: isMobile ? "56px" : "64px",
            height: isMobile ? "56px" : "64px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s",
            boxShadow: "0 4px 20px rgba(139,92,246,0.4)",
            position: "relative",
          }}
        >
          {isChatOpen ? (
            <FaTimes size={isMobile ? 22 : 26} color="white" />
          ) : (
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <FaComments size={isMobile ? 22 : 26} color="white" />
            </motion.div>
          )}
        </button>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            variants={chatVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: "fixed",
              bottom: dimensions.bottom,
              right: dimensions.right,
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: dimensions.borderRadius,
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              overflow: "hidden",
              zIndex: 999,
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${getBorderColor()}`,
              width: dimensions.width,
              height: dimensions.height,
            }}
          >
            {/* Chat Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                padding: isMobile ? "12px 16px" : "14px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "rgba(139,92,246,0.2)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiMessageCircle size={18} color="white" />
                </div>
                <div>
                  <h3
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: isMobile ? "14px" : "16px",
                      fontWeight: "600",
                    }}
                  >
                    Community Chat
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginTop: "2px",
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        background: isConnected ? "#10b981" : "#ef4444",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: isConnected ? "pulse 1.5s infinite" : "none",
                      }}
                    />
                    <span
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "10px",
                      }}
                    >
                      {isConnected ? "Connected" : "Connecting..."}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                {!isExpanded && !isMobile && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleMinimize}
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                      borderRadius: "8px",
                      width: "28px",
                      height: "28px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      transition: "all 0.2s",
                    }}
                  >
                    {isMinimized ? (
                      <FaExpand size={12} />
                    ) : (
                      <FaMinus size={12} />
                    )}
                  </motion.button>
                )}
                {!isMobile && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleExpand}
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                      borderRadius: "8px",
                      width: "28px",
                      height: "28px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      transition: "all 0.2s",
                    }}
                  >
                    {isExpanded ? (
                      <FaCompress size={12} />
                    ) : (
                      <FaExpand size={12} />
                    )}
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleChat}
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "none",
                    borderRadius: "8px",
                    width: "28px",
                    height: "28px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    transition: "all 0.2s",
                  }}
                >
                  <FaTimes size={12} />
                </motion.button>
              </div>
            </div>

            {/* Chat Content */}
            {!isMinimized && <ChatWindow isExpanded={isExpanded} />}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Muted Warning Toast */}
      <AnimatePresence>
        {isMuted && isChatOpen && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            style={{
              position: "fixed",
              bottom: "100px",
              right: isMobile ? "20px" : "460px",
              left: isMobile ? "20px" : "auto",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              color: "white",
              padding: "12px 20px",
              borderRadius: "12px",
              fontSize: "12px",
              zIndex: 998,
              textAlign: "center",
              boxShadow: "0 4px 14px 0 rgba(239,68,68,0.4)",
            }}
          >
            <FaShieldAlt
              size={12}
              style={{ marginRight: "8px", display: "inline" }}
            />
            You are muted. Cannot send messages.
          </motion.div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </>
  );
};

export default ChatWidget;
